import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import CustomModal from '@/components/common/elements/Modal';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { api } from '@/api/repository/axios';
import type {
  CouponDiscountType,
  InstructorCoupon,
  InstructorCouponDistribution,
  InstructorCouponPayload,
} from '@/api/repository/types/types';
import {
  INSTRUCTOR_COMMISSION_RATE,
  MAX_RATE_PERCENT,
  MIN_PAYMENT_AMOUNT,
  calcDiscount,
  errorMessageOf,
  generateCouponCode,
  sanitizeCouponCode,
  untilText,
  won,
} from '@/components/domain/coupon/couponFormat';
import { designSystem } from '@/styles/theme';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { focusField } from '@/common/focusField';
import DateTimePicker from '@/components/common/datePicker/DateTimePicker';
import { FilterPanel, FilterPanelControls, useIsMobileWidth } from '@/components/common/filter';

/*
 * 강사 "쿠폰 만들기" 팝업 (시안 r5-instructor-popup) — 항상 강사 부담, 이 세미나 전용.
 * 이름 · 정액/정률 · 값 · (정률) 최대 할인 · 기간(기본 오늘~모집 마감) · 최대 수량 · 코드(자동 생성, 수정 가능)
 * · 환불 복원 체크 · 지급 방식(받기/코드만) · 미리보기(참가자 결제 → 내 정산 기준 → 예상 지급).
 * 수정 모드: 발급이 있으면(editableValue=false) 이름·기간·수량만 바꿀 수 있다.
 * 초안 모드(seminarId 없음 = 신규 등록): 같은 폼·같은 검증(정가 = 폼 참가비, 모집 마감 = 폼 값)으로 저장 대신 초안에 담는다.
 *   초안은 세미나 등록 요청의 coupons[] 로 함께 보내 서버가 세미나 저장 뒤 만든다.
 */

const PRIMARY: string = designSystem.colors.primary;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BurdenPill = styled.span`
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 999px;
  background: #fff4e6;
  color: #c2410c;
  font-size: 12px;
  font-weight: 800;
`;

/** 결제에 쓰인 쿠폰 — 안의 입력·버튼을 통째로 잠근다(배치는 본문과 같게) */
const ReadOnlyFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 700;
  color: #4b5563;

  b {
    color: ${FORM_COLOR.danger};
    margin-left: 2px;
  }

  small {
    font-weight: 500;
    color: #9ca3af;
    margin-left: 4px;
  }
`;

/** 입력 칸 — UI 가이드 3장(52px·반경 12·테두리 #D3DBD8, 오류면 빨강 테두리 + 칸 바로 아래 문구) */
const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: 1px solid ${FORM_COLOR.border};
  border-radius: 12px;
  font-family: inherit;
  font-size: 16px;
  color: ${FORM_COLOR.text};
  box-sizing: border-box;
  background: #fff;
  min-width: 0;

  &::placeholder {
    color: ${FORM_COLOR.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${PRIMARY};
  }

  &[aria-invalid='true'] {
    border-color: ${FORM_COLOR.danger};
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }
`;

const Toggle = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  height: 44px;
  border: none;
  background: ${({ active }) => (active ? PRIMARY : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#4b5563')};
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: ${({ active }) => (active ? 0.7 : 1)};
  }
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const Help = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: #6b7280;
  word-break: keep-all;
  white-space: pre-line;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${FORM_COLOR.danger};
  white-space: pre-line;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  cursor: pointer;
  word-break: keep-all;

  input {
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    flex-shrink: 0;
    accent-color: ${PRIMARY};
  }

  span small {
    color: #6b7280;
    font-size: 13px;
  }
`;

const Preview = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8faf9;
  border: 1px solid #eef1f0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
  font-variant-numeric: tabular-nums;

  div {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  b {
    color: #111827;
  }
`;

const Actions = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
`;

/** 버튼 — UI 가이드 4장(높이 52·반경 14, 보조는 흰 바탕 테두리, 비활성 #B8CFCB) */
const ActionButton = styled.button<{ primary?: boolean }>`
  flex: 1;
  height: 52px;
  border-radius: 14px;
  border: ${({ primary }) => (primary ? 'none' : `1px solid ${FORM_COLOR.border}`)};
  background: ${({ primary }) => (primary ? PRIMARY : '#fff')};
  color: ${({ primary }) => (primary ? '#fff' : '#374151')};
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: ${({ primary }) => (primary ? FORM_COLOR.primaryDisabled : '#fff')};
    cursor: default;
  }
`;

const pad = (n: number): string => String(n).padStart(2, '0');
const dateOnly = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const d: Date = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : dateOnly(d);
};
/** 서버 LocalDateTime 형식 "2026-10-14T23:00:00" */
const toLocalDateTime = (d: Date): string =>
  `${dateOnly(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
const digits = (value: string): string => value.replace(/[^0-9]/g, '');

/** 세미나 등록 전 만든 쿠폰 초안 — 등록 요청 coupons[] 한 칸(서버 InstructorCouponRequest 와 같은 필드) */
export interface CouponDraft {
  /** 화면 목록 key (서버로 보내지 않는다) */
  key: string;
  payload: InstructorCouponPayload;
}

/** 한 세미나에 등록하면서 만들 수 있는 쿠폰 수(서버 상한) */
export const MAX_DRAFT_COUPONS: number = 10;

const COUPON_CODE_PATTERN: RegExp = /^[A-Z0-9]{6,12}$/;

/** `YYYY-MM-DDTHH:mm[:ss]` → 로컬 Date */
const parseDateTime = (value?: string | null): Date | null => {
  if (!value) return null;
  const d: Date = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export interface CouponCheckContext {
  /** 정가(참가비) */
  listPrice: number;
  /** 모집 마감 — 사용 기간 기본값·상한 */
  recruitmentEnd?: string | null;
  /** 받은 사람이 있어 이름·기간·수량만 바꿀 수 있는 쿠폰 */
  valueLocked?: boolean;
  /** 새로 만드는 쿠폰(초안 포함) — 끝나는 날이 지났는지·모집 마감을 넘는지 본다 */
  isNew: boolean;
  /** 고치기: 원래 끝나는 날 — 끝나는 날을 바꿨을 때만 새 쿠폰과 같은 검사(서버와 같은 규칙, 안 바꾼 옛 값은 통과) */
  originalEndsAt?: string | null;
  /** 이미 쓰는 코드(다른 초안) */
  takenCodes?: string[];
  now?: Date;
}

/** 쿠폰 팝업 칸 — 검증에 걸린 칸으로 포커스하려고 문구와 함께 돌려준다 */
export type CouponField = 'name' | 'value' | 'maxDiscount' | 'startDate' | 'endDate' | 'maxIssues' | 'code';

export interface CouponProblem {
  message: string;
  field: CouponField;
}

/**
 * 강사 쿠폰 입력 검사 — 서버 SeminarCouponManageService.validate 와 같은 규칙(초안이 등록 뒤 실패하지 않게).
 * 문제가 있으면 문장마다 줄바꿈한 문구와 틀린 칸, 없으면 null.
 */
export const checkCouponPayload = (payload: InstructorCouponPayload, ctx: CouponCheckContext): CouponProblem | null => {
  const now: Date = ctx.now ?? new Date();
  const name: string = payload.name.trim();
  if (!name) return { message: '쿠폰 이름을 입력해 주세요.', field: 'name' };
  if (name.length > 60) return { message: '쿠폰 이름은 60자까지예요.', field: 'name' };
  if (!ctx.valueLocked) {
    const value: number = payload.discountValue;
    if (value <= 0) return { message: payload.discountType === 'RATE' ? '할인율을 입력해 주세요.' : '할인 금액을 입력해 주세요.', field: 'value' };
    if (payload.discountType === 'RATE' && value > MAX_RATE_PERCENT) return { message: `정률은 최대 ${MAX_RATE_PERCENT}%까지예요.`, field: 'value' };
    if (payload.discountType === 'FIXED' && ctx.listPrice > 0 && value > ctx.listPrice - MIN_PAYMENT_AMOUNT) {
      const max: number = Math.max(0, ctx.listPrice - MIN_PAYMENT_AMOUNT);
      return {
        message: `참가비 ${won(ctx.listPrice)}에서는 최대 ${won(max)}까지 할인할 수 있어요.\n결제 금액이 ${won(MIN_PAYMENT_AMOUNT)} 이상 남아야 해요.`,
        field: 'value',
      };
    }
    const code: string = payload.code ?? '';
    if (!COUPON_CODE_PATTERN.test(code)) return { message: '코드는 영문 대문자·숫자 6~12자예요.', field: 'code' };
    if (ctx.takenCodes?.includes(code)) return { message: '같은 코드의 쿠폰이 이미 있어요.\n코드를 바꿔 주세요.', field: 'code' };
  }
  if (payload.maxIssues != null && payload.maxIssues < 1) return { message: '최대 수량은 1장 이상이에요.\n비우면 무제한이에요.', field: 'maxIssues' };

  const recruitEnd: Date | null = parseDateTime(ctx.recruitmentEnd);
  const endsAt: Date | null = parseDateTime(payload.endsAt) ?? recruitEnd;
  if (!endsAt) return { message: '사용 기간(끝나는 날)을 정해 주세요.', field: 'endDate' };
  const startsAt: Date = parseDateTime(payload.startsAt) ?? now;
  if (endsAt.getTime() <= startsAt.getTime()) return { message: '끝나는 날은 시작보다 뒤여야 해요.', field: 'endDate' };
  // 끝나는 날을 새로 정했는지 — 새 쿠폰은 항상, 고치기는 날짜가 원래와 다를 때
  const endChanged: boolean = ctx.isNew || (payload.endsAt ?? '').slice(0, 10) !== (ctx.originalEndsAt ?? '').slice(0, 10);
  if (endChanged && endsAt.getTime() <= now.getTime()) return { message: '끝나는 날이 이미 지났어요.', field: 'endDate' };
  if (endChanged && recruitEnd && endsAt.getTime() > recruitEnd.getTime()) {
    return { message: `사용 기간은 모집 마감(${untilText(ctx.recruitmentEnd, true)})까지로 정해 주세요.`, field: 'endDate' };
  }
  return null;
};

/** 문구만 필요할 때(초안 목록·등록 전 확인) */
export const validateCouponPayload = (payload: InstructorCouponPayload, ctx: CouponCheckContext): string | null =>
  checkCouponPayload(payload, ctx)?.message ?? null;

/** 서버 거절 문구로 틀린 칸을 짐작한다(서버 응답에 field 가 오면 그걸 쓴다) */
const couponFieldFromMessage = (message: string): CouponField | null => {
  const rules: Array<[RegExp, CouponField]> = [
    [/코드/, 'code'],
    [/이름/, 'name'],
    [/최대 할인/, 'maxDiscount'],
    [/할인|정률|정액/, 'value'],
    [/수량|장을 줬/, 'maxIssues'],
    [/끝나는 날|사용 기간|기간/, 'endDate'],
  ];
  return rules.find(([pattern]) => pattern.test(message))?.[1] ?? null;
};

interface InstructorCouponDialogProps {
  open: boolean;
  /** 저장된 세미나 id — 없으면 초안 모드(서버에 만들지 않고 onDraftSaved 로 돌려준다) */
  seminarId?: string;
  /** 참가비(정가) — 미리보기 */
  listPrice: number;
  /** 모집 마감 — 기간 기본값 */
  recruitmentEnd?: string | null;
  /** 수정할 쿠폰 — 없으면 새로 만들기 */
  editing?: InstructorCoupon | null;
  /** 고칠 초안(새 등록 초안 또는 수정 화면에서 저장 전 담아 둔 쿠폰) */
  editingDraft?: CouponDraft | null;
  /** 다른 초안·쿠폰이 쓰는 코드(중복 방지) */
  takenCodes?: string[];
  onClose: () => void;
  onSaved?: (coupon: InstructorCoupon | null) => void;
  /** 초안 모드: 검사를 통과한 요청 본문 */
  onDraftSaved?: (payload: InstructorCouponPayload) => void;
  /**
   * 수정 화면에서 모집 마감·참가비를 바꾸고 아직 저장하지 않았을 때 — 서버에 바로 보내지 않고(서버는 저장된 값으로 검사한다)
   * 화면 값으로 검사해 담아 둔다. 새 쿠폰은 onDraftSaved, 기존 쿠폰 고치기는 onDeferredUpdate. 세미나를 저장하면 반영된다.
   */
  defer?: boolean;
  /** 고치기: 서버에 저장된 원래 끝나는 날(보류 값을 얹어 열 때 — 없으면 editing.endsAt) */
  originalEndsAt?: string | null;
  /** 보류 모드에서 기존 쿠폰 고치기: 서버에 보낼 내용(발급 뒤면 이름·기간·수량만)과 전체 값 */
  onDeferredUpdate?: (update: Partial<InstructorCouponPayload>, payload: InstructorCouponPayload) => void;
}

const InstructorCouponDialog: React.FC<InstructorCouponDialogProps> = ({
  open,
  seminarId,
  listPrice,
  recruitmentEnd,
  editing,
  editingDraft,
  takenCodes,
  onClose,
  onSaved,
  onDraftSaved,
  defer = false,
  onDeferredUpdate,
  originalEndsAt,
}) => {
  const draftMode: boolean = !seminarId;
  const mobile: boolean = useIsMobileWidth();
  const recruitEndDate: Date | null = useMemo(() => {
    if (!recruitmentEnd) return null;
    const d: Date = new Date(recruitmentEnd);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [recruitmentEnd]);

  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState<CouponDiscountType>('FIXED');
  const [value, setValue] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [maxIssues, setMaxIssues] = useState('');
  const [code, setCode] = useState('');
  const [restore, setRestore] = useState(false);
  const [distribution, setDistribution] = useState<InstructorCouponDistribution>('CLAIM');
  const [error, setError] = useState('');
  // 틀린 칸 — aria-invalid 와 포커스
  const [errorField, setErrorField] = useState<CouponField | null>(null);
  const [saving, setSaving] = useState(false);

  // 열 때마다 초기화(수정이면 기존 값)
  useEffect(() => {
    if (!open) return;
    setError('');
    setErrorField(null);
    // 초안 고치기(새 등록 초안·수정 화면에서 저장 전 담아 둔 쿠폰)
    const draft: InstructorCouponPayload | undefined = editingDraft?.payload;
    if (draft) {
      setName(draft.name);
      setDiscountType(draft.discountType);
      setValue(String(draft.discountValue));
      setMaxDiscount(draft.maxDiscountAmount ? String(draft.maxDiscountAmount) : '');
      setStartDate(draft.startsAt ? draft.startsAt.slice(0, 10) : dateOnly(new Date()));
      setEndDate(draft.endsAt ? draft.endsAt.slice(0, 10) : '');
      setMaxIssues(draft.maxIssues ? String(draft.maxIssues) : '');
      setCode(draft.code ?? generateCouponCode());
      setRestore(draft.restoreOnRefund);
      setDistribution(draft.distribution);
    } else if (editing) {
      setName(editing.name);
      setDiscountType(editing.discountType);
      setValue(String(editing.discountValue));
      setMaxDiscount(editing.maxDiscountAmount ? String(editing.maxDiscountAmount) : '');
      setStartDate(toDateInput(editing.startsAt));
      setEndDate(toDateInput(editing.endsAt));
      setMaxIssues(editing.maxIssues ? String(editing.maxIssues) : '');
      setCode(editing.code);
      setRestore(editing.restoreOnRefund);
      setDistribution(editing.distribution);
    } else {
      setName('');
      setDiscountType('FIXED');
      setValue('');
      setMaxDiscount('');
      setStartDate(dateOnly(new Date()));
      setEndDate(recruitEndDate ? dateOnly(recruitEndDate) : '');
      setMaxIssues('');
      setCode(generateCouponCode());
      setRestore(false);
      setDistribution('CLAIM');
    }
  }, [open, editing, editingDraft, draftMode, recruitEndDate]);

  // 결제에 쓰인 쿠폰(editable=false) — 전 칸 읽기 전용, 저장 없음. 중지·재개는 목록에서
  const fullyLocked: boolean = Boolean(editing) && editing?.editable === false;
  const valueLocked: boolean = fullyLocked || (Boolean(editing) && editing?.editableValue === false);
  const numericValue: number = Number(value) || 0;
  const numericMax: number | null = maxDiscount ? Number(maxDiscount) : null;
  // 미리보기 — 저장된 쿠폰을 값 그대로 보고 있으면 서버 계산(previewPrice·settlementBasis·expectedPayout),
  // 새로 만들거나 값을 바꾸면 여기서 계산: 정산 기준 = 정가 − 할인 × 분담률(강사 쿠폰 1), 지급 = 기준 − round(기준 × 15%)
  const valueUnchanged: boolean = Boolean(editing)
    && editing?.discountType === discountType
    && editing?.discountValue === numericValue
    && (editing?.maxDiscountAmount ?? null) === (discountType === 'RATE' ? numericMax : null);
  // 화면 참가비가 저장된 정가와 다르면(수정 화면에서 바꾸고 아직 저장 전) 서버 미리보기는 옛 참가비 기준이라 쓰지 않는다
  const priceUnchanged: boolean = typeof editing?.listPrice !== 'number' || editing.listPrice === listPrice;
  const useServerPreview: boolean = valueUnchanged && priceUnchanged && typeof editing?.previewPrice === 'number';
  const shareRate: number = editing?.instructorShareRate ?? 1;
  const discount: number = calcDiscount(discountType, numericValue, discountType === 'RATE' ? numericMax : null, listPrice);
  const paid: number = useServerPreview ? (editing?.previewPrice as number) : Math.max(0, listPrice - discount);
  const basis: number = useServerPreview && typeof editing?.settlementBasis === 'number'
    ? editing.settlementBasis
    : Math.round(listPrice - discount * shareRate);
  const payout: number = useServerPreview && typeof editing?.expectedPayout === 'number'
    ? editing.expectedPayout
    : basis - Math.round(basis * INSTRUCTOR_COMMISSION_RATE);
  const previewList: number = useServerPreview && typeof editing?.listPrice === 'number' ? editing.listPrice : listPrice;

  // 달력에서 고를 수 있는 날 — 새 쿠폰은 오늘부터 모집 마감까지(검증 규칙: 끝나는 날 > 지금, ≤ 모집 마감).
  // 기존 쿠폰은 원래 시작일(이미 지났으면 그날)과 오늘 중 이른 날부터 — 지난 시작일은 그대로 둘 수 있고, 다른 지난 날로는 못 옮긴다
  const isNewCoupon: boolean = draftMode || !editing;
  const today: string = dateOnly(new Date());
  const originalStart: string = editing ? toDateInput(editing.startsAt) : '';
  const periodMinDate: string = isNewCoupon || !originalStart || originalStart > today ? today : originalStart;
  // 끝나는 날 상한은 새 쿠폰·고치기 모두 폼 모집 마감(쿠폰 기준 하나). 옛 쿠폰이 이미 뒤로 끝나도 칸엔 그대로 보이고 안 건드리면 통과
  const periodMaxDate: string | undefined = recruitEndDate ? dateOnly(recruitEndDate) : undefined;

  const buildEndsAt = (): string | null => {
    if (!endDate) return null; // 비우면 서버가 모집 마감으로
    // 고치기에서 끝나는 날을 안 바꿨으면 원래 값 그대로(서버가 '바꾸지 않음'으로 본다)
    if (editing?.endsAt && endDate === toDateInput(editing.endsAt)) return editing.endsAt;
    if (recruitEndDate && endDate === dateOnly(recruitEndDate)) return toLocalDateTime(recruitEndDate);
    return `${endDate}T23:59:59`;
  };

  /** 칸 → 입력 id (할인 값은 정액·정률에 따라 다른 칸) */
  const fieldId = (field: CouponField): string => {
    const ids: Record<CouponField, string> = {
      name: 'coupon-name',
      value: discountType === 'RATE' ? 'coupon-rate' : 'coupon-value',
      maxDiscount: 'coupon-max',
      startDate: 'coupon-start',
      endDate: 'coupon-end',
      maxIssues: 'coupon-max-issues',
      code: 'coupon-code',
    };
    return ids[field];
  };

  /** 값을 고치면 그 칸의 오류(빨간 표시·문구)를 지운다 — 칸을 짚지 않은 오류는 아무 칸이나 고치면 지운다 */
  const clearErrorFor = (field: CouponField): void => {
    if (!error && !errorField) return;
    const period: boolean = (errorField === 'startDate' || errorField === 'endDate') && (field === 'startDate' || field === 'endDate');
    if (errorField === null || errorField === field || period) {
      setError('');
      setErrorField(null);
    }
  };

  /** 틀린 칸 바로 아래 문구 */
  const fieldError = (...fields: CouponField[]) =>
    error && errorField && fields.includes(errorField) ? <ErrorText>{error}</ErrorText> : null;

  /** 문구를 보이고 그 칸으로 스크롤·포커스(얼럿만 띄우고 끝내지 않는다) */
  const showProblem = (message: string, field: CouponField | null): void => {
    setError(message);
    setErrorField(field);
    if (field) window.setTimeout(() => focusField(fieldId(field)), 0);
  };

  const handleSave = async (): Promise<void> => {
    if (fullyLocked) return;
    if (startDate && endDate && startDate > endDate) {
      showProblem('사용 기간을 확인해 주세요.', 'endDate');
      return;
    }
    const payload: InstructorCouponPayload = {
      name: name.trim(),
      discountType,
      discountValue: numericValue,
      maxDiscountAmount: discountType === 'RATE' && numericMax ? numericMax : null,
      startsAt: startDate ? `${startDate}T00:00:00` : null,
      endsAt: buildEndsAt(),
      maxIssues: maxIssues ? Number(maxIssues) : null,
      code: code || null,
      distribution,
      restoreOnRefund: restore,
    };
    const problem: CouponProblem | null = checkCouponPayload(payload, {
      listPrice,
      recruitmentEnd,
      valueLocked,
      isNew: draftMode || !editing,
      originalEndsAt: originalEndsAt !== undefined ? originalEndsAt : editing?.endsAt ?? null,
      takenCodes,
    });
    if (problem) {
      showProblem(problem.message, problem.field);
      return;
    }
    setErrorField(null);
    // 새 등록 초안, 수정 화면에서 담아 둔 쿠폰 고치기, 보류 중 새 쿠폰 — 서버에 보내지 않고 초안으로
    if (draftMode || editingDraft || (defer && !editing)) {
      setError('');
      onDraftSaved?.(payload);
      return;
    }
    if (defer && editing) {
      setError('');
      onDeferredUpdate?.(
        valueLocked ? { name: payload.name, startsAt: payload.startsAt, endsAt: payload.endsAt, maxIssues: payload.maxIssues } : payload,
        payload,
      );
      return;
    }
    if (!seminarId) return;
    setSaving(true);
    setError('');
    try {
      if (editing) {
        const update: Partial<InstructorCouponPayload> = valueLocked
          ? { name: payload.name, startsAt: payload.startsAt, endsAt: payload.endsAt, maxIssues: payload.maxIssues }
          : payload;
        const res = await api.coupon.instructorUpdate(editing.id, update);
        if (res?.code !== 0) {
          showProblem(res?.message || '쿠폰을 저장하지 못했어요.', res?.message ? couponFieldFromMessage(res.message) : null);
          return;
        }
        onSaved?.(res.result ?? null);
      } else {
        const res = await api.coupon.instructorCreate(seminarId, payload);
        if (res?.code !== 0) {
          showProblem(res?.message || '쿠폰을 만들지 못했어요.', res?.message ? couponFieldFromMessage(res.message) : null);
          return;
        }
        onSaved?.(res.result ?? null);
      }
    } catch (e: unknown) {
      const message: string = errorMessageOf(e, editing ? '쿠폰을 저장하지 못했어요.' : '쿠폰을 만들지 못했어요.');
      const serverField = (e as { response?: { data?: { field?: string } } })?.response?.data?.field as CouponField | undefined;
      showProblem(message, serverField ?? couponFieldFromMessage(message));
    } finally {
      setSaving(false);
    }
  };

  const title: string = fullyLocked ? '쿠폰 보기' : editing || editingDraft ? '쿠폰 수정' : '쿠폰 만들기';

  /** [닫기][만들기·담기·저장] — PC 는 창 맨 아래, 폰은 시트 하단 고정 */
  const actions = (close: () => void) => (
    <Actions>
      <ActionButton type="button" onClick={close} disabled={saving}>닫기</ActionButton>
      {!fullyLocked && (
        <ActionButton type="button" primary onClick={() => void handleSave()} disabled={saving}>
          {saving ? <ButtonSpinner /> : draftMode || defer || editingDraft ? (editingDraft || editing ? '고치기' : '담기') : editing ? '저장' : '만들기'}
        </ActionButton>
      )}
    </Actions>
  );

  const content = (footer: React.ReactNode) => (
      <Body>
        <BurdenPill>할인은 강사 부담</BurdenPill>

        {/* 결제에 쓰인 쿠폰이면 아래 입력 전부 잠금(fieldset disabled) */}
        <ReadOnlyFieldset disabled={fullyLocked}>

        <Field>
          <FieldLabel htmlFor="coupon-name">쿠폰 이름<b>*</b></FieldLabel>
          <Input id="coupon-name" aria-invalid={errorField === 'name'} value={name} maxLength={60} placeholder="예) 얼리버드 1만 원" onChange={(e) => { setName(e.target.value); clearErrorFor('name'); }} />
          {fieldError('name')}
        </Field>

        <Field>
          <FieldLabel as="div">할인 방식<b>*</b></FieldLabel>
          <Toggle role="radiogroup" aria-label="할인 방식">
            <ToggleButton type="button" role="radio" aria-checked={discountType === 'FIXED'} active={discountType === 'FIXED'} disabled={valueLocked} onClick={() => { if (discountType !== 'FIXED') { setDiscountType('FIXED'); setValue(''); clearErrorFor('value'); } }}>
              정액
            </ToggleButton>
            <ToggleButton type="button" role="radio" aria-checked={discountType === 'RATE'} active={discountType === 'RATE'} disabled={valueLocked} onClick={() => { if (discountType !== 'RATE') { setDiscountType('RATE'); setValue(''); clearErrorFor('value'); } }}>
              정률
            </ToggleButton>
          </Toggle>
        </Field>

        {discountType === 'FIXED' ? (
          <Field>
            <FieldLabel htmlFor="coupon-value">할인 금액(원)<b>*</b></FieldLabel>
            <Input
              id="coupon-value"
              aria-invalid={errorField === 'value'}
              inputMode="numeric"
              placeholder="10,000"
              disabled={valueLocked}
              value={value ? Number(value).toLocaleString() : ''}
              onChange={(e) => { setValue(digits(e.target.value).slice(0, 9)); clearErrorFor('value'); }}
            />
            {fieldError('value')}
          </Field>
        ) : (
          <TwoCols>
            <Field>
              <FieldLabel htmlFor="coupon-rate">할인율(%)<b>*</b></FieldLabel>
              <Input
                id="coupon-rate"
                aria-invalid={errorField === 'value'}
                inputMode="numeric"
                placeholder="20"
                disabled={valueLocked}
                value={value}
                onChange={(e) => { setValue(digits(e.target.value).slice(0, 2)); clearErrorFor('value'); }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="coupon-max">최대 할인(원)<small>선택</small></FieldLabel>
              <Input
                id="coupon-max"
                aria-invalid={errorField === 'maxDiscount'}
                inputMode="numeric"
                placeholder="15,000"
                disabled={valueLocked}
                value={maxDiscount ? Number(maxDiscount).toLocaleString() : ''}
                onChange={(e) => { setMaxDiscount(digits(e.target.value).slice(0, 9)); clearErrorFor('maxDiscount'); }}
              />
            </Field>
          </TwoCols>
        )}
        {discountType === 'RATE' && fieldError('value', 'maxDiscount')}
        <Help>
          {/* 고른 할인 방식에 맞는 안내만(정액인데 정률 안내가 보이던 것) */}
          {discountType === 'RATE' ? (
            <>
              <div>정률은 최대 {MAX_RATE_PERCENT}%까지예요.</div>
              <div>100% 쿠폰은 만들 수 없어요(결제가 일어나지 않아 참가 확정이 안 돼요).</div>
            </>
          ) : listPrice > 0 ? (
            <div>참가비 {won(listPrice)}에서 최대 {won(Math.max(0, listPrice - MIN_PAYMENT_AMOUNT))}까지 할인할 수 있어요.</div>
          ) : null}
          {valueLocked && !fullyLocked && <div>받은 사람이 있어 이름·기간·수량만 바꿀 수 있어요.</div>}
        </Help>

        <Field>
          <FieldLabel as="div">사용 기간<b>*</b></FieldLabel>
          {/* 한 칸 달력(기간) — 시작일 → 종료일, 하루면 시작일만. 검증 이동 대상 id(coupon-start·coupon-end)는 그대로 */}
          <div id="coupon-start">
            <div id="coupon-end">
              <DateTimePicker
                mode="range"
                title="사용 기간"
                placeholder="시작일 ~ 종료일"
                start={startDate}
                // 끝나는 날을 비워 둔 초안은 모집 마감까지 — 칸에도 그렇게 보인다(안 건드리면 비운 채 저장)
                end={endDate || (recruitEndDate ? dateOnly(recruitEndDate) : '')}
                onChange={(start: string, end: string) => {
                  setStartDate(start);
                  setEndDate(end);
                  clearErrorFor('endDate');
                }}
                minDate={periodMinDate}
                maxDate={periodMaxDate}
                hasError={errorField === 'startDate' || errorField === 'endDate'}
                inModal
                showNotes={false}
              />
            </div>
          </div>
          {fieldError('startDate', 'endDate')}
          {recruitEndDate
            ? <Help>{`모집 마감(${untilText(recruitmentEnd, true)}) 뒤로는 정할 수 없어요.`}</Help>
            : <Help>끝나는 날을 꼭 정해 주세요.</Help>}
        </Field>

        <Field>
          <FieldLabel htmlFor="coupon-max-issues">최대 수량<small>비우면 무제한</small></FieldLabel>
          <Input
            id="coupon-max-issues"
            aria-invalid={errorField === 'maxIssues'}
            inputMode="numeric"
            placeholder="무제한"
            value={maxIssues}
            onChange={(e) => { setMaxIssues(digits(e.target.value).slice(0, 6)); clearErrorFor('maxIssues'); }}
          />
          {fieldError('maxIssues')}
        </Field>

        <Field>
          <FieldLabel htmlFor="coupon-code">코드<small>— 자동 생성, 바꿔도 돼요</small></FieldLabel>
          <Input
            id="coupon-code"
            aria-invalid={errorField === 'code'}
            value={code}
            disabled={valueLocked}
            autoCapitalize="characters"
            onChange={(e) => { setCode(sanitizeCouponCode(e.target.value)); clearErrorFor('code'); }}
          />
          {fieldError('code')}
        </Field>

        <Field>
          <FieldLabel as="div">지급 방식</FieldLabel>
          <Toggle role="radiogroup" aria-label="지급 방식">
            <ToggleButton type="button" role="radio" aria-checked={distribution === 'CLAIM'} active={distribution === 'CLAIM'} disabled={valueLocked} onClick={() => setDistribution('CLAIM')}>
              받기
            </ToggleButton>
            <ToggleButton type="button" role="radio" aria-checked={distribution === 'CODE'} active={distribution === 'CODE'} disabled={valueLocked} onClick={() => setDistribution('CODE')}>
              코드만
            </ToggleButton>
          </Toggle>
          <Help>{distribution === 'CLAIM' ? '세미나 상세에 보이고 참가자가 직접 받아요.' : '상세에는 안 보이고 코드를 아는 사람만 쓸 수 있어요.'}</Help>
        </Field>

        <CheckLabel>
          <input type="checkbox" checked={restore} disabled={valueLocked} onChange={(e) => setRestore(e.target.checked)} />
          <span>
            환불되면 쿠폰을 다시 쓸 수 있게 <small>(끄면 환불돼도 쿠폰은 소멸. 폐강·강사 거절은 항상 돌아와요)</small>
          </span>
        </CheckLabel>

        {previewList > 0 && (
          <Preview aria-label="미리보기">
            <div>
              <span>참가자 결제</span>
              <span>{previewList.toLocaleString()} → <b>{won(paid)}</b></span>
            </div>
            <div>
              <span>내 정산 기준</span>
              <span>{won(basis)}</span>
            </div>
            <div>
              <span>예상 지급(수수료 15% 뺀)</span>
              <span><b>{won(payout)}</b> / 1명</span>
            </div>
          </Preview>
        )}

        </ReadOnlyFieldset>

        {error && <ErrorText role="alert">{error}</ErrorText>}

        {footer}

        {/* 안내는 팝업 맨 아래 */}
        {draftMode && <Help>세미나를 등록하면 이 쿠폰이 함께 발급돼요.</Help>}
        {!draftMode && (defer || editingDraft) && !fullyLocked && (
          <Help>{editing ? '세미나를 저장하면 이렇게 바뀌어요.' : '세미나를 저장하면 이 쿠폰이 발급돼요.'}</Help>
        )}
        {fullyLocked && <Help>{'결제에 쓰인 쿠폰은 바꿀 수 없어요.\n중지하고 새 쿠폰을 만들어 주세요.'}</Help>}
      </Body>
  );

  // 폰: 아래에서 올라오는 시트(UI 가이드 5장) — 사용 기간 달력 시트가 그 위에 뜨도록 modal 층, 하단 버튼은 시트 안 고정
  if (mobile) {
    return (
      <FilterPanel
        open={open}
        anchorEl={null}
        title={title}
        onClose={() => { if (!saving) onClose(); }}
        showClose
        sheetZIndex={designSystem.zIndex.modal}
        sheetMaxHeight="92dvh"
        renderFooter={(controls: FilterPanelControls) => actions(() => { if (!saving) controls.close(); })}
      >
        {content(null)}
      </FilterPanel>
    );
  }

  return (
    <CustomModal
      title={title}
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      width="min(460px, calc(100vw - 2rem))"
      height="auto"
      // 사용 기간 달력(팝업)은 body 에 뜬다 — 포커스를 창 안에 가두면 달력에서 키보드·포커스가 안 먹는다
      disableEnforceFocus
    >
      {content(actions(onClose))}
    </CustomModal>
  );
};

export default InstructorCouponDialog;
